import React, { useMemo } from "react";

import { ANNOTATION_COLORS, ANNOTATION_OPACITY } from "@perceptron/constants/colors";
import { BoxAnnotation, Coordinates, AnnotationSource } from "@perceptron/core/types";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { normalizedToPercentage, calculateBoundingBoxDimensions, COORDINATE_SPACE } from "@perceptron/utils/math";
import { decode } from "he";

import { useAnnotationInteractions } from "@demo/hooks/annotations/useAnnotationInteractions";
import { ZOOM_BOX_TYPE } from "@demo/utils/annotations";
import { strings } from "@demo/utils/strings";

const AVERAGE_CHARACTER_WIDTH = 8;

interface BoundingBoxProps {
  pointBox: {
    id: string;
    label?: string;
    coordinates: Coordinates;
    boxType?: BoxAnnotation["boxType"];
    source: AnnotationSource;
  };
}

const getVerticalClass = (overflow: { top: boolean; bottom: boolean }) => {
  if ((!overflow.top && !overflow.bottom) || (overflow.bottom && !overflow.top)) {
    return "-top-1 -translate-y-full";
  }
  if (overflow.top && !overflow.bottom) {
    return "-bottom-1 translate-y-full";
  }
  return "top-0.5 !left-0.5";
};

const getHorizontalClass = (overflow: { left: boolean; right: boolean }) => {
  if ((!overflow.left && !overflow.right) || (overflow.right && !overflow.left)) {
    return "-left-[2px] text-left";
  }
  if (overflow.left && !overflow.right) {
    return "-right-[2px] text-right";
  }
  return "left-0.5";
};

/** The zoom label is centred over its box, and slides to the edge it would otherwise cross. */
const getZoomHorizontalClass = (overflow: { left: boolean; right: boolean }) => {
  if (overflow.left && !overflow.right) {
    return "left-0";
  }
  if (overflow.right && !overflow.left) {
    return "right-0";
  }
  return "left-1/2 -translate-x-1/2";
};

export const BoundingBox = React.memo(function BoundingBox({ pointBox }: BoundingBoxProps) {
  const {
    isHighlighted,
    isCollectionHighlighted,
    isDimmed,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
    highlightedAnnotationIds,
    clickedAnnotationId,
  } = useAnnotationInteractions();

  const getBoundingBoxStyle = (boxId: string) => {
    if (isHighlighted(boxId) || isCollectionHighlighted([boxId])) {
      return {
        backgroundColor: `rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, 0)`,
        borderColor: ANNOTATION_COLORS.PRIMARY,
      };
    }
    if (isDimmed(boxId)) {
      return {
        backgroundColor: `rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, ${ANNOTATION_OPACITY.FILL_DIMMED})`,
        borderColor: `rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, 0.2)`,
      };
    }
    return {
      backgroundColor: `rgba(${ANNOTATION_COLORS.PRIMARY_RGB}, ${ANNOTATION_OPACITY.FILL_DEFAULT})`,
      borderColor: ANNOTATION_COLORS.PRIMARY,
    };
  };

  const dimensions = calculateBoundingBoxDimensions(
    pointBox.coordinates.top_left.x,
    pointBox.coordinates.top_left.y,
    pointBox.coordinates.bottom_right.x,
    pointBox.coordinates.bottom_right.y,
  );

  const isSinglePointHighlighted = useMemo(() => {
    return isHighlighted(pointBox.id) && (highlightedAnnotationIds?.length === 1 || !highlightedAnnotationIds);
  }, [isHighlighted, highlightedAnnotationIds, pointBox.id]);

  const willLabelOverflow = useMemo(() => {
    const horizontally =
      pointBox.label &&
      pointBox.label.length * AVERAGE_CHARACTER_WIDTH > COORDINATE_SPACE - pointBox.coordinates.top_left.x;

    // The zoom label is about 280 units wide on the 0–1000 grid; centred, it crosses an edge the box centre is within 140 of.
    const zoomLabelHalfWidth = 140;
    const boxCenterX = (pointBox.coordinates.top_left.x + pointBox.coordinates.bottom_right.x) / 2;

    return {
      vertical: {
        top: pointBox.coordinates.top_left.y < 40,
        bottom: pointBox.coordinates.bottom_right.y > COORDINATE_SPACE - 40,
      },
      horizontal: {
        left: !!horizontally,
        right: !horizontally,
      },
      zoomHorizontal: {
        left: boxCenterX < zoomLabelHalfWidth,
        right: boxCenterX > COORDINATE_SPACE - zoomLabelHalfWidth,
      },
    };
  }, [
    pointBox.label,
    pointBox.coordinates.top_left.x,
    pointBox.coordinates.top_left.y,
    pointBox.coordinates.bottom_right.x,
    pointBox.coordinates.bottom_right.y,
  ]);

  const isZoomBox = pointBox.boxType === ZOOM_BOX_TYPE;
  const isClicked = clickedAnnotationId === pointBox.id;

  // A zoom box is drawn only while selected: corner brackets and its label, no fill or border.
  if (isZoomBox && !isClicked) {
    return null;
  }

  return (
    <div
      className={cn(
        "dark absolute transition-all duration-200 ease-in-out",
        !isZoomBox && "rounded-[2px] border-2",
        // A selected zoom box takes no pointer events: a click inside it reaches the image and clears the
        // selection like a background click, rather than toggling the box.
        isZoomBox && isClicked ? "pointer-events-none" : "cursor-pointer",
      )}
      data-testid={`bounding-box-${pointBox.id}`}
      id={`bounding-box-${pointBox.id}`}
      key={pointBox.id}
      style={{
        left: `${normalizedToPercentage(pointBox.coordinates.top_left.x)}%`,
        top: `${normalizedToPercentage(pointBox.coordinates.top_left.y)}%`,
        width: `${normalizedToPercentage(dimensions.width)}%`,
        height: `${normalizedToPercentage(dimensions.height)}%`,
        zIndex: Math.round(COORDINATE_SPACE * COORDINATE_SPACE - dimensions.width * dimensions.height),
        ...(isZoomBox
          ? {
              backgroundColor: "transparent",
              borderColor: "transparent",
            }
          : getBoundingBoxStyle(pointBox.id)),
      }}
      onClick={() => handleClick(pointBox.id, pointBox.source)}
      onMouseEnter={() => handleMouseEnter([pointBox.id], pointBox.source)}
      onMouseLeave={() => handleMouseLeave(pointBox.id)}
    >
      {isZoomBox && isClicked && (
        <>
          <div className="absolute left-0 top-0 h-4 w-4 animate-zoom-clamp-in-tl border-l-[2px] border-t-[2px] border-foreground-primary" />
          <div className="absolute right-0 top-0 h-4 w-4 animate-zoom-clamp-in-tr border-r-[2px] border-t-[2px] border-foreground-primary" />
          <div className="absolute bottom-0 left-0 h-4 w-4 animate-zoom-clamp-in-bl border-b-[2px] border-l-[2px] border-foreground-primary" />
          <div className="absolute bottom-0 right-0 h-4 w-4 animate-zoom-clamp-in-br border-b-[2px] border-r-[2px] border-foreground-primary" />
        </>
      )}

      {(isSinglePointHighlighted || isClicked) && isZoomBox && (
        <div
          className={cn(
            "bg-foreground-primary/10 absolute z-[1000] flex animate-zoom-label-fade-in items-center gap-2 whitespace-nowrap rounded-[40px] border border-foreground-primary p-0.5 backdrop-blur-[4px]",
            // A box spanning the whole height has no room above or below; the label sits inside, top-left.
            willLabelOverflow.vertical.top && willLabelOverflow.vertical.bottom
              ? "left-2 top-2"
              : cn(
                  "transform",
                  getVerticalClass(willLabelOverflow.vertical),
                  getZoomHorizontalClass(willLabelOverflow.zoomHorizontal),
                ),
          )}
          data-testid="zoom-box-label"
        >
          <div className="flex items-center gap-1.5 rounded-3xl bg-white/15 px-1.5 py-0.5">
            <Icon className="h-3 w-3 text-foreground-primary" name="scan-eye" />
            <span className="text-xs font-medium text-foreground-primary">{strings.toolCall.zoom.label}</span>
          </div>
          <span className="pr-1.5 text-xs text-white">
            ({pointBox.coordinates.top_left.x}, {pointBox.coordinates.top_left.y}) x (
            {pointBox.coordinates.bottom_right.x}, {pointBox.coordinates.bottom_right.y})
          </span>
        </div>
      )}
      {(isSinglePointHighlighted || isClicked) && !isZoomBox && (
        <div
          className={cn(
            "absolute z-[1000] transform whitespace-nowrap rounded-sm px-2 py-0.5 text-xs text-white",
            getVerticalClass(willLabelOverflow.vertical),
            getHorizontalClass(willLabelOverflow.horizontal),
          )}
          style={{ backgroundColor: ANNOTATION_COLORS.PRIMARY }}
        >
          {decode(pointBox.label ?? "")}
        </div>
      )}
    </div>
  );
});
